import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import StatTile from '../ui/StatTile';
import { useSelectStyles } from '../../utils/selectStyles';
import SortableTable from '../SortableTable';

const formatYMD = (d) => new Date(d).toISOString().split('T')[0];

const isInternalClient = (clientName) => {
  if (!clientName) return false;
  const upper = clientName.toUpperCase();
  return upper.includes('GLOBE') || upper.includes('PRIMER IMPACTO');
};

const StatisticsUsersTab = ({ startDate, endDate, clientFilter, fetchWithAuth, apiBaseUrl, toolFilter, includeInternal }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [daily, setDaily] = useState([]);
  const [metric, setMetric] = useState('visits'); // 'visits' | 'events'
  const selectStyles = useSelectStyles();
  const [usersSummary, setUsersSummary] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const summaryCacheRef = useRef(new Map());

  // Load users list
  useEffect(() => {
    const loadUsers = async () => {
      const params = new URLSearchParams({ startDate: formatYMD(startDate), endDate: formatYMD(endDate) });
      if (clientFilter?.value) params.set('client', clientFilter.value);
      const resp = await fetchWithAuth(`${apiBaseUrl}/api/dwh/bi/users?${params.toString()}`);
      if (!resp.ok) return;
      const json = await resp.json();
      setUsers((json.data || []).map(u => ({ value: u, label: u })));
    };

    loadUsers();
  }, [startDate, endDate, clientFilter, fetchWithAuth, apiBaseUrl]);

  // Load users summary when no selectedUser
  useEffect(() => {
    const loadSummary = async () => {
      if (selectedUser?.value) { setUsersSummary([]); return; }
      const key = JSON.stringify({
        start: formatYMD(startDate),
        end: formatYMD(endDate),
        client: clientFilter?.value || null,
        tool: toolFilter?.value || null
      });

      // Serve from cache if available
      if (summaryCacheRef.current.has(key)) {
        setUsersSummary(summaryCacheRef.current.get(key));
        setLoadingSummary(false);
        return;
      }

      setLoadingSummary(true);
      try {
        const params = new URLSearchParams({ startDate: formatYMD(startDate), endDate: formatYMD(endDate) });
        if (clientFilter?.value) params.set('client', clientFilter.value);
        if (toolFilter?.value) params.set('tool', toolFilter.value);
        const resp = await fetchWithAuth(`${apiBaseUrl}/api/dwh/bi/tools/stats/users-summary?${params.toString()}`);
        if (!resp.ok) { setUsersSummary([]); return; }
        const json = await resp.json();
        const normalized = (json.data || []).map(r => ({
          username: r.username,
          client: r.client,
          visits: Number(r.totalVisits || 0),
          events: Number(r.totalEvents || 0)
        }))
        // Save in cache
        summaryCacheRef.current.set(key, normalized);
        setUsersSummary(normalized);
      } finally {
        setLoadingSummary(false);
      }
    };
    loadSummary();
  }, [selectedUser, startDate, endDate, clientFilter, toolFilter, fetchWithAuth, apiBaseUrl]);

  // Load daily aggregates when user changes
  useEffect(() => {
    const loadDaily = async () => {
      if (!selectedUser?.value) { setDaily([]); return; }
      const params = new URLSearchParams({ startDate: formatYMD(startDate), endDate: formatYMD(endDate), user: selectedUser.value });
      if (clientFilter?.value) params.set('client', clientFilter.value);
      const resp = await fetchWithAuth(`${apiBaseUrl}/api/dwh/bi/tools/stats/user-daily?${params.toString()}`);
      if (!resp.ok) return;
      const json = await resp.json();
      setDaily(json.data || []);
    };
    loadDaily();
  }, [selectedUser, startDate, endDate, clientFilter, fetchWithAuth, apiBaseUrl]);

  const fullRangeDays = useMemo(() => {
    const days = [];
    if (!startDate || !endDate) return days;
    const cur = new Date(startDate);
    cur.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    while (cur.getTime() <= end.getTime()) {
      days.push(formatYMD(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return days;
  }, [startDate, endDate]);

  const chartData = useMemo(() => {
    // Initialize all days in range with zeros
    const map = new Map(fullRangeDays.map(d => [d, { day: d, visits: 0, events: 0 }]));

    // Aggregate API rows onto the full range
    daily
      .filter(d => !toolFilter || (d.TOOL || d.tool) === toolFilter.value)
      .forEach(d => {
        const key = formatYMD(d.DAY || d.day);
        const acc = map.get(key) || { day: key, visits: 0, events: 0 };
        acc.visits += Number(d.TOTAL_VISITS || d.total_visits || d.totalVisits || 0);
        acc.events += Number(d.TOTAL_EVENTS || d.total_events || d.totalEvents || 0);
        map.set(key, acc);
      });

    return Array.from(map.values()).sort((a, b) => a.day.localeCompare(b.day));
  }, [daily, toolFilter, fullRangeDays]);

  const userStats = useMemo(() => {
    if (!daily.length) return { visits: 0, events: 0, tools: 0, mainTool: '-' };
    const visitSum = daily.reduce((acc, d) => acc + (Number(d.TOTAL_VISITS || d.total_visits || d.totalVisits || 0)), 0);
    const eventSum = daily.reduce((acc, d) => acc + (Number(d.TOTAL_EVENTS || d.total_events || d.totalEvents || 0)), 0);
    const byTool = new Map();
    daily.forEach(d => {
      const toolKey = d.TOOL || d.tool || '-';
      byTool.set(toolKey, (byTool.get(toolKey) || 0) + (Number(d.TOTAL_VISITS || d.total_visits || d.totalVisits || 0)));
    });
    const mainTool = Array.from(byTool.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || '-';
    return { visits: visitSum, events: eventSum, tools: byTool.size, mainTool };
  }, [daily]);

  // Build dynamic columns (per tool) and rows (per day) for the visits table
  const toolNames = useMemo(() => {
    const names = new Set();
    daily.forEach(d => {
      const t = d.TOOL || d.tool;
      if (!t) return;
      if (toolFilter?.value && t !== toolFilter.value) return;
      names.add(t);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [daily, toolFilter]);

  const visitsTableColumns = useMemo(() => {
    const cols = [{ key: 'day', label: 'Jour' }];
    toolNames.forEach(t => cols.push({ key: t, label: t }));
    cols.push({ key: 'total', label: 'Total' });
    return cols;
  }, [toolNames]);

  const visitsTableData = useMemo(() => {
    // Initialize one row per day with zeros
    const rows = fullRangeDays.map(d => {
      const row = { day: d };
      toolNames.forEach(t => { row[t] = 0; });
      row.total = 0;
      return row;
    });

    const dayToRow = new Map(rows.map(r => [r.day, r]));

    daily.forEach(d => {
      const tool = d.TOOL || d.tool;
      if (!tool) return;
      if (toolNames.length > 0 && !toolNames.includes(tool)) return;
      const day = formatYMD(d.DAY || d.day);
      const visits = Number(d.TOTAL_VISITS || d.total_visits || d.totalVisits || 0);
      const row = dayToRow.get(day);
      if (!row) return;
      row[tool] = (row[tool] || 0) + visits;
      row.total += visits;
    });

    return rows.filter(r => (r.total || 0) > 0);
  }, [daily, toolNames, fullRangeDays]);

  return (
    <div className="space-y-4">
      <div className="">
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Utilisateur</label>
          <Select
            value={selectedUser}
            onChange={setSelectedUser}
            options={users}
            isClearable
            isSearchable
            placeholder="Sélectionner un utilisateur..."
            classNamePrefix="react-select"
            styles={selectStyles}
            menuPortalTarget={document.body}
          />
        </div>
      </div>

      {selectedUser && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatTile label="Visites" value={userStats.visits.toLocaleString('fr-FR')} className="text-2xl font-bold text-blue-800 dark:text-blue-200" tileClassName="bg-blue-50 dark:bg-blue-900/20" />
            <StatTile label="Événements" value={userStats.events.toLocaleString('fr-FR')} className="text-2xl font-bold text-green-800 dark:text-green-200" tileClassName="bg-green-50 dark:bg-green-900/20" />
            <StatTile label="Outils utilisés" value={userStats.tools} className="text-2xl font-bold text-purple-800 dark:text-purple-200" tileClassName="bg-purple-50 dark:bg-purple-900/20" />
            <StatTile label="Outil principal" value={userStats.mainTool} className="text-2xl font-bold text-amber-800 dark:text-amber-200" tileClassName="bg-amber-50 dark:bg-amber-900/20" />
          </div>

          <div className="p-4 rounded-lg border dark:border-gray-700 h-72">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold dark:text-white">Activité journalière</h3>
              <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setMetric('visits')}
                  className={`px-3 py-1 text-xs font-medium rounded-md ${metric === 'visits' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-300'}`}
                >
                  Visites
                </button>
                <button
                  onClick={() => setMetric('events')}
                  className={`ml-1 px-3 py-1 text-xs font-medium rounded-md ${metric === 'events' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-300'}`}
                >
                  Événements
                </button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey={metric} fill={metric === 'visits' ? '#3B82F6' : '#10B981'} name={metric === 'visits' ? 'Visites' : 'Événements'} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="mb-8"></div>

      {!selectedUser && (
        <div className="space-y-3 mt-4 pt-4">
          <div className="p-4 rounded-lg border dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold dark:text-white">Utilisateurs (période)</h3>
            </div>
            <SortableTable
              data={usersSummary.filter(u => includeInternal || !isInternalClient(u.client))}
              columns={[
                { key: 'username', label: 'Nom' },
                { key: 'client', label: 'Client' },
                { key: 'visits', label: 'Visits' },
                { key: 'events', label: 'Events' }
              ]}
              emptyMessage={loadingSummary ? 'Chargement...' : 'Aucune donnée'}
              countLabel={`Utilisateurs: ${usersSummary.filter(u => includeInternal || !isInternalClient(u.client)).length}`}
              exportable={true}
              exportFilename={`utilisateurs_${formatYMD(startDate)}_${formatYMD(endDate)}.csv`}
              withSum={true}
              disableFilters
              onRowClick={(row) => setSelectedUser({ value: row.username, label: row.username })}
              initialSortBy={{ key: 'visits', direction: 'descending' }}
            />
          </div>
        </div>
      )}

      {selectedUser && (
        <div className="space-y-3 mt-4 pt-4">
          <div className="p-4 rounded-lg border dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold dark:text-white">Visites par outil (par jour)</h3>
            </div>
            <SortableTable
              data={visitsTableData}
              columns={visitsTableColumns}
              emptyMessage={"Aucune donnée pour ce périmètre"}
              countLabel={`Jours: ${visitsTableData.length}`}
              exportable={true}
              exportFilename={`visites_par_outil_${selectedUser?.value || 'utilisateur'}.csv`}
              globalSearch={true}
              withSum={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsUsersTab;


